import {
	CONSOLIDATOR_ID_BRM,
	CONSOLIDATOR_ID_VETCORE,
	CONSOLIDATOR_ID_THRIVE,
	COUNTRY_CA,
	CONSOLIDATOR_ID_RAREBREED,
	CONSOLIDATOR_ID_MVP,
	COUNTRY_US,
	PARTNER_ID_BRAVA
} from '../constants'
import { PAYMENT_BASE_URL } from '../config'
import { Practice } from 'gql/generated-types/custom-graphql'
import { validatePaymentIntentPollingInterval, validatePaymentIntentTimeout } from './util'
import CustomizedLabels from './CustomizedLabels'
import {
	isTerminalTippingEnabled as consolidatorIsTerminalTippingEnabled,
	getTerminalTippingPromptByDefault as consolidatorGetTerminalTippingPromptByDefault,
	isSavedCardOnlyEnabled as consolidatorIsSavedCardOnlyEnabled
} from './consolidator'

export function isCheckOnlyPractice(currentPracticeObject: IPractice) {
	return (
		(currentPracticeObject as any)?.checkOnly === 1 || currentPracticeObject?.settings?.checkOnly
	)
}

export const isBravaPractice = (practice: IPractice) =>
	practice?.partnerIds?.includes(PARTNER_ID_BRAVA) ?? false

export const isVetCorePractice = (practice: IPractice) =>
	practice?.consolidatorId === CONSOLIDATOR_ID_VETCORE

export const isMVPPractice = (practice: IPractice) =>
	practice?.consolidatorId === CONSOLIDATOR_ID_MVP

export const isVetspirePIMSPractice = (practice: IPractice) =>
	practice?.currentPims?.toLowerCase() === 'vetspire'

export const isCanadianPractice = (practice: IPractice) =>
	practice?.clinicCountry?.toUpperCase() === COUNTRY_CA

export const isUSPractice = (practice: IPractice) =>
	practice?.clinicCountry?.toUpperCase() === COUNTRY_US

export const shouldShowInstallmentPlansCA = (practice: IPractice) =>
	practice?.settings?.isInstallmentPlansEnabled && isCanadianPractice(practice)

export const shouldShowInstallmentPlans = (practice: IPractice) =>
	practice?.settings?.isInstallmentPlansEnabled

export const shouldShowStoredCardsReport = (practice: IPractice) =>
	practice?.settings?.isSavePaymentMethodsEnabled || practice?.settings?.isStoredCardsReportEnabled

export const shouldAllowStoringCardsOnlinePayment = (practice: IPractice) =>
	practice?.settings?.isSavePaymentMethodsEnabled

export const isStoredCardsReportEnabled = (practice: IPractice) =>
	practice?.settings?.isStoredCardsReportEnabled

export const shouldShowAppointmentRequests = (practice: IPractice) =>
	practice?.settings?.isAppointmentRequestsEnabled

export const practiceShouldShowDailyTotalsCardGrouping = (practice: IPractice) =>
	practice?.settings?.showDailyTotalsCardGrouping

export const shouldShowIndustryProgramsPage = (practice: IPractice) =>
	getSettingByName(practice, 'isIndustryPromoEnabled')

export const shouldShowPriorityCallList = (practice: IPractice) =>
	practice?.settings?.isPriorityCallListEnabled

export const shouldShowReputationManagement = (practice: IPractice) =>
	practice?.settings?.isReputationEnabled

export const shouldShowOptOuts = (practice: IPractice) => practice?.settings?.isOptOutsEnabled

export const shouldShowForms = (practice: IPractice) => practice?.settings?.isFormsEnabled

export const shouldRestrictRefundsForAdmins = (practice: IPractice) =>
	practice?.settings?.restrictRefundsToAdmins

// Communications Section in sidebar should show if any of the communication settings are enabled
export const shouldShowCommunicationsSection = (practice: IPractice) =>
	practice?.isChatEnabled === true ||
	practice?.settings?.isPriorityCallListEnabled === true ||
	practice?.settings?.isAppointmentRequestsEnabled === true ||
	practice?.settings?.isRemindersEnabled === true ||
	practice?.settings?.isOneTimeEmailsEnabled === true ||
	practice?.settings?.isReputationEnabled === true

export const shouldShowECheckReportingPage = (practice: IPractice) =>
	practice?.settings?.showECheckReportingPage ?? false

export const shouldShowManualPayment = (practice: IPractice) =>
	practice?.settings?.manualPaymentEnabled ?? true

export const isSurchargeEnabled = (practice: IPractice) =>
	(practice?.settings as any)?.surchargeEnabled || practice?.surchargeEnabled

export const isDonationEnabled = (practice: IPractice) => practice?.settings?.donationsEnabled

export const practiceIsTerminalTippingEnabled = (practice: IPractice) =>
	practice?.settings?.enableTerminalTipping

const practiceGetTerminalTippingPromptByDefault = (practice: IPractice) =>
	practice?.settings?.terminalTippingConfig?.promptByDefault

export const getTerminalTippingPromptByDefault = (
	practice: IPractice,
	consolidator?: IConsolidator
) =>
	!!(
		practiceGetTerminalTippingPromptByDefault(practice) ??
		consolidatorGetTerminalTippingPromptByDefault(consolidator)
	)

// Practice-level setting takes precedence; falls back to the consolidator-level flag
// when the practice hasn't set one explicitly. Shared so any page that needs to know
// whether terminal tipping is enabled uses the same resolution order.
export const isTerminalTippingEnabled = (practice: IPractice, consolidator?: IConsolidator) =>
	practiceIsTerminalTippingEnabled(practice) ?? consolidatorIsTerminalTippingEnabled(consolidator)

export const isInsurancePaymentBypassEnabled = (practice: IPractice) =>
	practice?.settings?.insurancePaymentBypassEnabled

export const shouldShowCheckPayments = (
	practice: IPractice,
	consolidatorHasCheckEnabled?: boolean
) => practice?.settings?.isCheckEnabled ?? consolidatorHasCheckEnabled ?? false

export const shouldShowTerminalPayments = (
	practice: IPractice,
	consolidatorHasTerminalEnabled?: boolean
) => practice?.settings?.isTerminalEnabled ?? consolidatorHasTerminalEnabled ?? true

export const shouldShowMonthlyStatements = (practice: IPractice) => {
	const consolidatorId = practice?.consolidatorId ?? null
	if (!consolidatorId) return true
	if (
		[
			CONSOLIDATOR_ID_MVP,
			CONSOLIDATOR_ID_BRM,
			CONSOLIDATOR_ID_VETCORE,
			CONSOLIDATOR_ID_THRIVE,
			CONSOLIDATOR_ID_RAREBREED
		].includes(consolidatorId)
	)
		return false
	return true
}

export const generatePracticePaymentLink = (practiceName, webPaymentPracticeId) =>
	`${PAYMENT_BASE_URL}/w/${practiceName.replace(/\s+/g, '-')}/${webPaymentPracticeId}`

export const shouldShowPaymentRequestRemindersFooterLink = (practice: IPractice) =>
	isVetCorePractice(practice) && !practice?.flags?.paymentRequestReminders

/**
 * Checks whether a specific feature is enabled based on the provided setting name and practice object.
 * Returns false ONLY if the settingName is explicitly defined in the practice settings object with a value of false.
 * Returns true if the settingName is not defined in the practice settings object or if its value is truthy.
 */
export const isFeatureEnabled = (practice, settingName) =>
	(practice?.settings?.[settingName] ?? true) && practice?.settings?.[settingName] !== false

export const getSettingByName = (
	practice: IPractice | undefined,
	settingName: string,
	defaultValue: boolean = false
) => practice?.settings?.[settingName] ?? defaultValue

export const getPracticeCreatedAtTimestamp = (currentPracticeObject) => {
	const createdAt = currentPracticeObject?.createdAt
	if (typeof createdAt === 'number') {
		return currentPracticeObject?.createdAt
	}
	if (typeof createdAt === 'string') {
		try {
			const date = new Date(createdAt)
			return Math.floor(date.getTime() / 1000)
		} catch {
			console.error('Could not parse createdAt')
			return 0
		}
	}
	return 0
}

export const isRoleChangeable = (practice: Practice) =>
	practice.isPaymentEnabled || practice.isRebateEnabled || !!practice.stripeAccountId

export const isFinancingEnabled = (practice: IPractice | Practice) => !!practice?.vetClinicId
export const isPaymentsEnabled = (practice: IPractice | Practice) => !!practice?.stripeAccountId
export const isFinancingOnlyPractice = (practice: IPractice | Practice, showPayments = true) =>
	isFinancingEnabled(practice) && (!isPaymentsEnabled(practice) || !showPayments)

// isDashboardEnabled is lending-only; consolidated equivalent is lending on (context API omits deleted ones)
export const hasFinancingDashboardAccess = (practice: IPractice, useConsolidated: boolean) => {
	const cp = practice?.consolidatedPractice
	return useConsolidated ? !!cp?.isLendingEnabled : !!practice?.isDashboardEnabled
}

export const blockAmexPayments = (practice: IPractice) =>
	getSettingByName(practice, 'blockAmex') === true

export const isIndependentPractice = (practice: IPractice) => !practice.consolidatorId

export const isRebateEnabled = (practice: IPractice) =>
	practice?.consolidatedPractice?.isRebateEnabled === true

export const isServerDrivenTerminalEnabled = (practice: IPractice) =>
	practice?.settings?.localTerminalConnection !== true

export const shouldHideReceiptModal = (practice: IPractice) =>
	practice?.settings?.disablePaymentReceiptModal

export const standardRateScheme = [
	{ value: null, name: 'Remove Rate Scheme' },
	{ value: 'FiveTier', name: 'FiveTier' },
	{ value: 'FiveTierPromo', name: 'FiveTierPromo' },
	{ value: 'FourTier', name: 'FourTier' },
	{ value: 'Tier1', name: 'Tier1' },
	{ value: 'Tier2', name: 'Tier2' },
	{ value: 'Tier3', name: 'Tier3' },
	{ value: 'Tier4', name: 'Tier4' },
	{ value: 'Vetspire', name: 'Vetspire' },
	{ value: 'Vetspire229', name: 'Vetspire229' },
	{ value: 'IC035Percent', name: 'IC + 0.35%' },
	{ value: 'IC050Percent', name: 'IC + 0.50%' },
	{ value: 'IC030Percent', name: 'IC + 0.30%' },
	{ value: 'IC020PercentPlus010', name: 'IC + 0.20% + $0.10' },
	{ value: 'IC017PercentPlus010', name: 'IC + 0.17% + $0.10' },
	{ value: 'IC014PercentPlus012', name: 'IC + 0.14% + $0.12' },
	{ value: 'IC018PercentPlus013', name: 'IC + 0.18% + $0.13' },
	{ value: 'IC025PercentPlus014', name: 'IC + 0.25% + $0.14' },
	{ value: 'IC030PercentPlus015', name: 'IC + 0.30% + $0.15' },
	{ value: 'IC035PercentPlus015', name: 'IC + 0.35% + $0.15' },
	{ value: 'EPICICPlus', name: 'EPIC IC +' }
]

export const isLendingAtCheckoutEnabled = (practice: IPractice) =>
	getSettingByName(practice, 'lendingCheckoutEnabled')

export const isStoredCardsV2Enabled = (practice?: IPractice) =>
	practice?.settings?.storedCardsV2Enabled

export const convertToUnifiedPracticeId = (practiceId?: string) => {
	return practiceId?.replace('acct_', 'prac_') || ''
}

export const paymentRequestExpires = (practice: IPractice) =>
	getSettingByName(practice, 'paymentRequestExpires') === true

export const isSurchargeRecurringPaymentsEnabled = (practice: IPractice) => {
	const recurringValue = practice?.settings?.surchargeRecurringPayments
	if (recurringValue !== undefined) {
		return recurringValue
	}
	if (isSurchargeEnabled(practice)) {
		return true
	}
	return undefined
}

export const shouldShowSavePaymentMethodCheckbox = (practice: IPractice) =>
	getSettingByName(practice, 'terminalSavedCardsEnabled')

export const getPracticePaymentIntentPollingIntervalMs = (
	practice: IPractice
): number | undefined => {
	const intervalMs = practice?.settings?.paymentIntentPollingIntervalMs
	return intervalMs !== undefined ? validatePaymentIntentPollingInterval(intervalMs) : undefined
}

export const getPracticePaymentIntentPollingTimeoutMs = (
	practice: IPractice
): number | undefined => {
	const timeoutMs = practice?.settings?.paymentIntentPollingTimeoutMs
	return timeoutMs !== undefined ? validatePaymentIntentTimeout(timeoutMs) : undefined
}

export const practiceHasExternalAccount = (practice?: IPractice): boolean => {
	const requiredInfo = practice?.additionalStripeRequiredInfo

	return Array.isArray(requiredInfo) ? !requiredInfo.includes('external_account') : false
}

// Terminal save-only mode: adds a "capture card at terminal" button to vault a
// card without charging. Recurring saved-card-only mode: replaces manual/Stripe
// card entry with the saved-card dropdown.
export const isTerminalSaveOnlyEnabled = (practice: IPractice): boolean | undefined =>
	practice?.settings?.terminalSaveOnlyEnabled

// Recurring saved-card-only mode: replaces manual/Stripe card entry with the saved-card dropdown. This is separate from terminal save-only mode since some practices may want to allow manual/Stripe entry for one-time payments but require saved cards for recurring payments.
export const isRecurringSavedCardOnlyEnabled = (practice: IPractice): boolean | undefined =>
	practice?.settings?.recurringSavedCardOnlyEnabled

// Card requests: the Collect Card Details tab texts or emails a link that saves a card
// and takes no payment. Returns undefined when the practice has not set the flag, so the
// caller can fall back to the consolidator — defaulting to false here would kill that fallback.
export const isSaveCardRequestEnabled = (practice?: IPractice): boolean | undefined =>
	practice?.settings?.saveCardRequestEnabled

// Saved payment methods v2: load a client's cards from every Stripe customer sharing their client id,
// instead of the newest Firestore customer flagged hasSavedCard. Undefined defers to the consolidator.
export const isSavedPaymentMethodsV2Enabled = (practice: IPractice): boolean | undefined =>
	practice?.settings?.useSavedPaymentMethodsV2

// Saved-card-only mode: phone payments take a saved card only. Independent of
// manualPaymentEnabled so a practice can switch manual entry off (PCI) and keep this on.
// The practice setting wins when set, including an explicit false (exempt practices);
// otherwise the consolidator flag applies, so it can be turned on consolidator-wide.
export const isSavedCardOnlyModeEnabled = (practice: IPractice, consolidator?: IConsolidator) =>
	practice?.settings?.savedCardOnly ?? consolidatorIsSavedCardOnlyEnabled(consolidator)

export const getCustomPhonePaymentsLabel = (practice?: IPractice, consolidator?: IConsolidator) =>
	practice && isSavedCardOnlyModeEnabled(practice, consolidator)
		? 'Saved Card Payment'
		: CustomizedLabels.getCustomLabel('phonePayments.label', 'Phone Payment')

export const shouldShowSavePaymentMethodDisclousure = (practice: IPractice): boolean | undefined =>
	practice?.settings?.terminalSavedCardsDisclosureEnabled
